import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import './FeedbackReport.css';

// Creator-facing scene-by-scene feedback on one draft (2026-10-27). The link goes
// out in the review DM from /draft-review. The token in the URL is the only key,
// so the backend (GET /api/draft-feedback/:token) returns just what the creator
// should see: the English notes, the frame each one points at, and the video.
// Creators open this from Slack, mostly on a phone; no emojis in the copy.

interface Scene {
  noteId: string;
  t: number;
  text: string;
  frameUrl: string | null;
}

interface Report {
  instagram: string | null;
  source: 'scheduler' | 'slack';
  day: number | null;
  slot: number | null;
  version: number;
  uploadedAt: string | null;
  playUrl: string | null;
  decision: 'approve' | 'reject' | null;
  note: string | null;
  reviewedAt: string | null;
  notes: Scene[];
}

const fmtT = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null);

const FeedbackReport: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [report, setReport] = useState<Report | null>(null);
  const [failed, setFailed] = useState<'not_found' | 'error' | null>(null);
  const [now, setNow] = useState(0);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    document.title = 'Draft feedback';
    fetch(`${API_URL}/api/draft-feedback/${encodeURIComponent(token || '')}`)
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (r.status === 404) { setFailed('not_found'); return; }
        if (!r.ok || !j.report) { setFailed('error'); return; }
        setReport(j.report);
      })
      .catch(() => setFailed('error'));
  }, [token]);

  // Shows the exact frame the note is about, paused, with the player in view.
  const jump = (t: number) => {
    const v = video.current;
    if (!v) return;
    v.pause();
    v.currentTime = t;
    v.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  if (failed) {
    return (
      <div className="fbr fbr--center">
        <p>{failed === 'not_found' ? 'This feedback link is not valid. Ask in Slack for a new one.' : 'Could not load the feedback. Please refresh the page.'}</p>
      </div>
    );
  }
  if (!report) {
    return <div className="fbr fbr--center"><p className="fbr-muted">Loading feedback…</p></div>;
  }

  // The scene being shown: the last note at or before the playhead.
  const active = report.notes.reduce<string | null>((acc, n) => (n.t <= now + 0.05 ? n.noteId : acc), null);
  const where = report.source === 'scheduler'
    ? `Day ${report.day}, ${report.slot === 1 ? '5pm' : '9pm'} slot${report.version > 1 ? `, version ${report.version}` : ''}`
    : 'Draft sent in Slack';
  const uploaded = fmtDate(report.uploadedAt);

  return (
    <div className="fbr">
      <div className="fbr-wrap">
        <header className="fbr-head">
          <p className="fbr-kicker">Draft feedback</p>
          <h1>{report.notes.length === 1 ? '1 note on your draft' : `${report.notes.length} notes on your draft`}</h1>
          <p className="fbr-meta">
            {report.instagram && <>@{report.instagram} · </>}
            {where}
            {uploaded && <> · uploaded {uploaded}</>}
          </p>
          {report.decision && (
            <span className={`fbr-status fbr-status--${report.decision}`}>
              {report.decision === 'approve' ? 'Approved, with notes' : 'Changes requested'}
            </span>
          )}
        </header>

        {report.note && <div className="fbr-note">{report.note}</div>}

        <div className="fbr-body">
          <div className="fbr-player">
            {report.playUrl ? (
              <>
                <video
                  ref={video}
                  src={report.playUrl}
                  controls
                  playsInline
                  preload="metadata"
                  onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
                  onSeeked={(e) => setNow(e.currentTarget.currentTime)}
                />
                <a className="fbr-file" href={report.playUrl} target="_blank" rel="noopener noreferrer">Open the video file</a>
              </>
            ) : (
              <div className="fbr-novideo">The video is no longer available.</div>
            )}
          </div>

          <ol className="fbr-scenes">
            {report.notes.map((n, i) => (
              <li key={n.noteId} className={`fbr-scene${active === n.noteId ? ' is-active' : ''}`}>
                <button className="fbr-frame" onClick={() => jump(n.t)} aria-label={`Show ${fmtT(n.t)} in the video`}>
                  {n.frameUrl && <img src={n.frameUrl} alt={`Frame at ${fmtT(n.t)}`} loading="lazy" />}
                </button>
                <div className="fbr-text">
                  <div className="fbr-stamp">
                    <span className="fbr-num">{i + 1}</span>
                    <button className="fbr-time" onClick={() => jump(n.t)}>{fmtT(n.t)}</button>
                  </div>
                  <p>{n.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default FeedbackReport;
